import React, { useState, useCallback, useEffect } from 'react';
import { PracticeScreen, KPracticeSettings } from '../types';
import IDoScreen from './IDoScreen';
import WeDoScreen from './WeDoScreen';
import YouDoScreen from './YouDoScreen';
import AccuracyScreen from './AccuracyScreen';
import Fluency15Screen from './Fluency15Screen';
import FluencyScreen from './FluencyScreen';
import { TRACK_RANGES } from '../../../types/constants';

interface PartWholePracticeProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
  initialPracticeType?: 'learning' | 'accuracy' | 'fluency15' | 'fluency';
}

const PartWholePractice: React.FC<PartWholePracticeProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
  initialPracticeType = 'learning'
}) => {
  // Determine initial screen based on practice type
  const getInitialScreen = (): PracticeScreen => {
    switch (initialPracticeType) {
      case 'accuracy':
        return 'accuracy';
      case 'fluency15':
        return 'fluency15';
      case 'fluency':
        return 'fluency';
      default:
        return 'ido';
    }
  };

  const [currentScreen, setCurrentScreen] = useState<PracticeScreen>(getInitialScreen());
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  // Convert fact ID to empty count (FACT1679 = 0, FACT1680 = 1, ..., FACT1684 = 5)
  const factToEmptyCount = useCallback((factId: string): number => {
    const factNumber = parseInt(factId.replace('FACT', ''));
    const [startFact] = TRACK_RANGES.TRACK_K_PARTWHOLE;
    return factNumber - startFact; // FACT1679 = 0, FACT1680 = 1, etc.
  }, []);

  // Generate all 6 K Part Whole facts (FACT1679 to FACT1684, empty counts 0-5)
  const getAllTrackFacts = useCallback((): { factId: string; digit: number }[] => {
    const [startFact, endFact] = TRACK_RANGES.TRACK_K_PARTWHOLE;
    const allFacts = [];
    for (let factNum = startFact; factNum <= endFact; factNum++) {
      allFacts.push({
        factId: `FACT${factNum}`,
        digit: factNum - startFact // 0, 1, 2, 3, 4, 5
      });
    }
    return allFacts;
  }, []);

  // Get current fact ID
  const getCurrentFactId = useCallback((): string => {
    return factsToPractice[currentFactIndex] || factsToPractice[0];
  }, [factsToPractice, currentFactIndex]);

  // Get current empty count
  const getCurrentEmptyCount = useCallback((): number => {
    return factToEmptyCount(getCurrentFactId());
  }, [getCurrentFactId, factToEmptyCount]);

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'ido':
        return (
          <IDoScreen
            settings={settings}
            factsToPractice={factsToPractice}
            onComplete={() => {
              setCurrentScreen('wedo');
              setCurrentFactIndex(0);
            }}
            onBack={onBack}
          />
        );

      case 'wedo': {
        // Convert facts to required format
        const factsWithEmptyCounts = factsToPractice.map(factId => ({
          factId,
          digit: factToEmptyCount(factId)  // digit field contains emptyCount for part whole
        }));
        return (
          <WeDoScreen
            settings={settings}
            factsToPractice={factsWithEmptyCounts}
            onComplete={() => {
              setCurrentScreen('youdo');
              setCurrentFactIndex(0);
            }}
            onBack={onBack}
          />
        );
      }

      case 'youdo': {
        // Convert facts to required format
        const youDoFactsWithEmptyCounts = factsToPractice.map(factId => ({
          factId,
          digit: factToEmptyCount(factId)
        }));
        return (
          <YouDoScreen
            settings={settings}
            factsToPractice={youDoFactsWithEmptyCounts}
            onComplete={() => {
              setCurrentScreen('accuracy');
            }}
            onBack={onBack}
          />
        );
      }

      case 'accuracy': {
        // Use ALL track facts for accuracy practice (not just the learning facts)
        return (
          <AccuracyScreen
            settings={settings}
            factsToPractice={getAllTrackFacts()}
            onComplete={() => {
              // AccuracyScreen handles pass/fail via page reload, this is fallback
              window.location.reload();
            }}
            onBack={onBack}
          />
        );
      }

      case 'fluency15': {
        // Use ALL track facts for fluency15 practice
        return (
          <Fluency15Screen
            settings={settings}
            factsToPractice={getAllTrackFacts()}
            onComplete={() => {
              // Fluency15Screen handles pass/fail via page reload, this is fallback
              window.location.reload();
            }}
            onBack={onBack}
          />
        );
      }

      case 'fluency':
        return (
          <FluencyScreen
            settings={settings}
            factsToPractice={factsToPractice.map(factId => ({
              factId,
              digit: factToEmptyCount(factId)
            }))}
            onComplete={onComplete}
            onBack={onBack}
          />
        );

      default:
        return null;
    }
  };

  // Handle empty factsToPractice array - navigate back via useEffect to avoid side effects during render
  useEffect(() => {
    if (factsToPractice.length === 0) {
      console.error('PartWholePractice: factsToPractice array is empty after practice started');
      onBack();
    }
  }, [factsToPractice.length, onBack]);

  // Guard against empty factsToPractice array - return stable fallback UI
  if (factsToPractice.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      {renderCurrentScreen()}
    </div>
  );
};

export default PartWholePractice;
